<template>
  <div :id="containerId"></div>
</template>

<script>
import { onMounted, ref } from 'vue';
import _ from 'lodash';
import Handsontable from 'handsontable';
import moment from 'moment';
import useModels from '@kanrisha/composables/useModels';

export default {
  props: {
    resource: { type: Object, default: () => {} },
    name: { type: String, default: 'resource-table-sheet' },
    options: { type: Object, default: () => {} },
  },
  emits: ['trigger:refresh'],
  setup(props, { emit }) {
    const tableData = ref([]);
    const cells = ref([]);
    const sources = ref({});
    const containerId = ref(props.name);
    const hotInstance = ref(null);
    const { storeModel: storeItem } = useModels({ resourceType: props.resource.meta.resourceType });

    const renderResourceTable = async () => {
      await initTableData();

      if (!cells.value) {
        return;
      }

      let tableContainer = document.getElementById(containerId.value);
      var tableOptions = _.merge(
        {
          data: tableData.value,
          rowHeaders: props.options.tableSheet?.rowHeaders ?? true,
          colHeaders: getColHeaders(),
          afterInit: function () {
            // this.scrollViewportTo(Object.values(tableData.value).length - 1, 1);
          },
          afterChange: function (change, source) {
            // todo bulk save support rather than saving each on individually
            if (source !== 'feedback' && source !== 'dateValidator' && change) {
              let rows = [];
              for (let key in change) {
                rows[change[key][0]] = change[key][0];
              }
              for (let key in rows.reverse()) {
                saveRow(rows[key]);
              }
            }
          },
          columns: getColumns(),
          colWidths: getColWidths(),
          hiddenColumns: getHiddenColumns(),
          rowHeights: 23,
          manualColumnResize: false,
          manualRowResize: false,
          stretchH: 'none',
          filters: true,
          dropdownMenu: true,
          minSpareRows: 1,
          licenseKey: 'non-commercial-and-evaluation',
        },
        props.options
      );

      hotInstance.value = new Handsontable(tableContainer, tableOptions);
    };

    const getColHeaders = () => {
      let listed = props.resource.meta.listed;
      let labels = ['ID'];
      for (let attribute in listed) {
        labels.push(props.resource.meta.labels[attribute]);
      }

      return labels;
    };

    const getColumns = () => {
      let columns = [{ data: 'id' }];
      for (let attribute in props.resource.meta.listed) {
        // todo: readonly, wordwrap, dates, relations, enums
        let options = {
          data: attribute,
          readOnly: !props.resource.meta.editable.includes(attribute),
          wordWrap: false,
        };

        if (props.resource.meta.listed[attribute] === 'time') {
          options.type = 'time';
          options.timeFormat = 'H:mm';
          options.correctFormat = true;
        } else if (['relation', 'belongs-to'].includes(props.resource.meta.listed[attribute])) {
          options.type = 'autocomplete';
          options.source = _.map(sources.value[attribute]?.data ?? [], 'data.attributes.name');
          options.strict = true;
        }

        columns.push(options);
      }

      return columns;

      // return [
      //   { data: 'id' },
      //   { data: 'date', type: 'date', dateFormat: 'L', correctFormat: true },
      //   { data: 'name', wordWrap: false },
      //   {
      //     data: 'time_account',
      //     wordWrap: false,
      //     type: 'autocomplete',
      //     source: _.map(timeAccounts.value.data, 'data.attributes.name'),
      //     strict: true
      //   },
      //   { data: 'category', wordWrap: false, readOnly: true },
      //   { data: 'duration' },
      //   { data: 'start', type: 'time', timeFormat: 'H:mm', correctFormat: true },
      //   { data: 'end', readOnly: true },
      // ];
    };

    const getColWidths = () => {
      let colWidths = [0];
      for (let attribute in props.resource.meta.listed) {
        // todo: readonly, wordwrap, dates, relations, enums
        colWidths.push(props.resource.meta.colWidths ? props.resource.meta.colWidths[attribute] ?? 50 : 50);
      }
      return colWidths;
    };

    const getHiddenColumns = () => {
      let hiddenKeys = [0];
      let index = 1;
      for (let attribute in props.resource.meta.listed) {
        if (
          props.resource.meta.colWidths &&
          (props.resource.meta.colWidths[attribute] === 0 || props.resource.meta.colWidths[attribute] === '0')
        ) {
          hiddenKeys.push(index);
        }
        index++;
      }

      return {
        columns: hiddenKeys,
      };
    };

    const saveRow = async (row) => {
      let resourceData = getDataByRow(row);
      let dataset = {
        data: {
          id: resourceData.id ?? null,
          attributes: {},
          type: props.resource.meta.resourceType,
        },
      };
      for (let attribute in props.resource.meta.listed) {
        if (props.resource.meta.editable.includes(attribute)) {
          switch (props.resource.meta.listed[attribute]) {
            case 'relation':
            case 'belongs-to':
              var relatedResource = _.filter(sources.value[attribute]?.data ?? [], {
                data: { attributes: { model_title: resourceData[attribute] } },
              })[0];
              dataset.data.attributes[`${attribute}_id`] = relatedResource ? parseInt(relatedResource.data.id) : null;
              break;
            default:
              if (props.options?.presets) {
                dataset.data.attributes[attribute] = props.options?.presets[attribute] ?? null;
              }
              if (!dataset.data.attributes[attribute]) {
                dataset.data.attributes[attribute] =
                  props.resource.meta.presets[attribute] ?? resourceData[attribute] ?? null;
              }
          }
        }
      }
      let params = {};
      if (props.options.relation) {
        params['relation'] = props.options.relation.name;
        params['parent'] = props.options.relation.parentType;
      }
      let storedItem = await storeItem(dataset, props.resource.meta.resourceType, params);
      if (!storedItem) {
        hotInstance.value.alter('remove_row', parseInt(row), 1);
      } else {
        refreshRow(row, storedItem);
      }
      emit('trigger:refresh');
    };

    const refreshRow = (row, storedActivity) => {
      let dataset = getResourceDataForTable(storedActivity);
      hotInstance.value.setDataAtCell(row, 0, dataset['id'], 'feedback');
      let index = 1;
      for (let attribute in props.resource.meta.listed) {
        hotInstance.value.setDataAtCell(row, index, dataset[attribute], 'feedback');
        index++;
      }
    };

    const initTableData = async () => {
      tableData.value = [];
      for (let key in props.resource.data) {
        let item = props.resource.data[key];
        let dataset = getResourceDataForTable(item);
        tableData.value.push(dataset);
      }
    };

    const getResourceDataForTable = (item) => {
      let dataset = { id: item.data.id };
      for (let attribute in props.resource.meta.listed) {
        dataset[attribute] = getAttributeForTable(attribute, item);
      }

      return dataset;
    };

    const getAttributeForTable = (attribute, item) => {
      switch (props.resource.meta.listed[attribute]) {
        case 'relation':
        case 'belongs-to':
          return item.data.relations[attribute]?.title;
        case 'date':
          // todo: use date-formatting service, respect your locale!
          return moment(item.data.attributes[attribute]).format('YYYY-MM-DD');
        case 'time':
          if (/^\d{2}:\d{2}$/.test(item.data.attributes[attribute])) {
            return item.data.attributes[attribute];
          }

          // todo: use date-formatting service, respect your locale!
          return moment(item.data.attributes[attribute]).format('HH:mm');
        default:
          return item.data.attributes[attribute];
      }
    };

    const getDataByRow = (row) => {
      return tableData.value[row];
    };

    onMounted(async () => {
      for (let relation in props.resource.meta.belongsTo) {
        if (props.resource.meta.listed[relation]) {
          let { models, fetchModels } = useModels({ resourceType: props.resource.meta.belongsTo[relation] }); // @meeseeks
          // todo add filters from relations resource schema here
          await fetchModels({ size: 200 });
          sources.value[relation] = models.value;
        }
      }
      await renderResourceTable();
    });

    return {
      containerId,
    };
  },
};
</script>
